import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { Contact, Conversation, GroupMember, GroupRecord, MessageEvent } from "@veto/domain";
import { mentionToken } from "@veto/domain";
import { loadEnterSends } from "../appearancePrefs";
import { getMeta } from "@veto/storage";
import {
  Avatar,
  IconAttach,
  IconBack,
  IconDetails,
  IconDirect,
  IconDoc,
  IconEmoji,
  IconEye,
  IconGif,
  IconImage,
  IconMore,
  IconPhone,
  IconPoll,
  IconSearch,
  IconSend,
  IconShield,
  IconSticker,
} from "@veto/ui";
import { MessageBubble } from "./MessageBubble";
import { VoiceNoteButton } from "./VoiceNoteButton";
import { EmojiPicker, type MediaTab } from "./EmojiPicker";
import { groupMessagesByDay } from "../messaging/dayGroups";
import { DEFAULT_WINDOW, nextWindow, visibleSlice } from "../messaging/messageWindow";
import { CALL_FEATURE_STATUS } from "@veto/protocol";
import { useProfileMediaUrl } from "../profile/profileMedia";

export function ChatPane({
  activeConv,
  activeContact,
  messages,
  chatQuery,
  setChatQuery,
  draft,
  setDraft,
  replyTo,
  editing,
  clearComposerMeta,
  messagesEndRef,
  voiceUrlFor,
  onBack,
  onToggleDetails,
  onOpenDetails,
  onSend,
  onAttachImage,
  onAttachDocument,
  onViewOnceImage,
  onSendSticker,
  onSendStickerFile,
  onSendGifFile,
  onVoice,
  onVoiceError,
  onDirect,
  onReply,
  onReact,
  onEdit,
  onDeleteMe,
  onDeleteEveryone,
  onForward,
  onStar,
  onPin,
  onOpenViewOnce,
  onCreatePoll,
  onVotePoll,
  onAcceptRequest,
  onDeclineRequest,
  onAcceptGroupInvite,
  onDeclineGroupInvite,
  activeGroup = null,
  contacts = [],
  selfKeyHex = "",
  unreadAtOpen = 0,
  focusMessageId = null,
  onFocusDone,
  onReport,
  disappearTimer = 0,
}: {
  /** The chat's disappearing timer (seconds), shown in the header. */
  disappearTimer?: number;
  /** Report & block the contact of this 1:1 chat (optionally about one message / from a request). */
  onReport?: (opts: { reported?: MessageEvent; fromRequest?: boolean }) => void;
  /** Bring this message into view (and flash it) once it's loaded — starred list, search. */
  focusMessageId?: string | null;
  onFocusDone?: () => void;
  /** Unread count when the chat was opened: divider above the first one, opened there. */
  unreadAtOpen?: number;
  activeConv: Conversation | null;
  /** Group chats: the group record (members, names) — sender names and membership checks. */
  activeGroup?: GroupRecord | null;
  contacts?: Contact[];
  /** This account's messaging key (membership check). */
  selfKeyHex?: string;
  onAcceptGroupInvite?: () => void;
  onDeclineGroupInvite?: (block: boolean) => void;
  activeContact: Contact | null;
  messages: MessageEvent[];
  chatQuery: string;
  setChatQuery: (v: string) => void;
  draft: string;
  setDraft: (v: string) => void;
  replyTo: MessageEvent | null;
  editing: MessageEvent | null;
  clearComposerMeta: () => void;
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
  voiceUrlFor: (eventId: string) => string | undefined;
  onBack: () => void;
  onToggleDetails: () => void;
  /** Desktop parity: right-click the chat header to open details. */
  onOpenDetails?: () => void;
  onSend: () => void;
  onAttachImage: (file: File) => void;
  onAttachDocument: (file: File) => void;
  onViewOnceImage: (file: File) => void;
  onSendSticker: (packId: string, stickerId: string) => void;
  onSendStickerFile: (file: File) => void;
  onSendGifFile: (file: File) => void;
  onVoice: (blob: Blob, durationMs: number) => void;
  onVoiceError?: (message: string) => void;
  onDirect: () => void;
  onReply: (m: MessageEvent) => void;
  onReact: (m: MessageEvent, emoji: string) => void;
  onEdit: (m: MessageEvent) => void;
  onDeleteMe: (m: MessageEvent) => void;
  onDeleteEveryone: (m: MessageEvent) => void;
  onForward: (m: MessageEvent) => void;
  onStar: (m: MessageEvent) => void;
  onPin: (m: MessageEvent) => void;
  onOpenViewOnce: (m: MessageEvent) => void;
  onCreatePoll?: () => void;
  onVotePoll?: (msg: MessageEvent, optionId: string) => void;
  /** Message request (first contact from someone we never added). */
  onAcceptRequest?: () => void;
  onDeclineRequest?: (block: boolean) => void;
}) {
  const [pickerTab, setPickerTab] = useState<MediaTab | null>(null);
  const [attachOpen, setAttachOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [windowCount, setWindowCount] = useState(DEFAULT_WINDOW);
  const toolsRef = useRef<HTMLDivElement | null>(null);
  const avatarUrl = useProfileMediaUrl(activeContact?.avatar ?? null);

  useEffect(() => {
    setWindowCount(DEFAULT_WINDOW);
    setAttachOpen(false);
    setMoreOpen(false);
    setPickerTab(null);
  }, [activeConv?.id]);

  useEffect(() => {
    if (!attachOpen && !moreOpen && !pickerTab) return;
    const onPointer = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      // The media picker popover renders as a sibling of .composer-tools —
      // treat interactions inside it as "inside" so sending stickers/GIFs
      // never dismisses the picker (ADR-0005 continuous-send behavior).
      const insidePicker =
        typeof target?.closest === "function" && target.closest(".media-picker") !== null;
      if (!toolsRef.current?.contains(e.target as Node) && !insidePicker) {
        setAttachOpen(false);
        setMoreOpen(false);
        setPickerTab(null);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setAttachOpen(false);
        setMoreOpen(false);
        setPickerTab(null);
      }
    };
    window.addEventListener("mousedown", onPointer);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onPointer);
      window.removeEventListener("keydown", onKey);
    };
  }, [attachOpen, moreOpen, pickerTab]);

  // Lab/dev harness: inject pad-fitting voice blobs (Playwright fake-mic often exceeds §9.5).
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const onTestVoice = (e: Event) => {
      const detail = (e as CustomEvent<{ blob: Blob; durationMs: number }>).detail;
      if (detail?.blob instanceof Blob) onVoice(detail.blob, detail.durationMs ?? 1000);
    };
    window.addEventListener("veto:test-voice", onTestVoice);
    return () => window.removeEventListener("veto:test-voice", onTestVoice);
  }, [onVoice]);

  // Hooks first (before the empty-state return): search, composer, group state, reading position.
  const searchQ = chatQuery.trim();
  const searchHits = searchQ
    ? messages
        .filter(
          (m) =>
            !m.deletedForEveryone &&
            !m.deletedForMe &&
            m.body.toLowerCase().includes(searchQ.toLowerCase()),
        )
        .map((m) => m.eventId)
    : [];
  const [searchPos, setSearchPos] = useState(0);
  const [flashId, setFlashId] = useState<string | null>(null);
  useEffect(() => {
    if (!focusMessageId) return;
    const idx = messages.findIndex((m) => m.eventId === focusMessageId);
    if (idx < 0) return; // not loaded yet
    if (messages.length - idx > windowCount) setWindowCount(messages.length - idx + 10);
    const t = window.setTimeout(() => {
      document
        .getElementById(`msg-${focusMessageId}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      setFlashId(focusMessageId);
      onFocusDone?.();
    }, 120);
    const clear = window.setTimeout(() => setFlashId(null), 1800);
    return () => {
      window.clearTimeout(t);
      window.clearTimeout(clear);
    };
  }, [focusMessageId, messages]);
  // A new term starts at the most recent match.
  useEffect(() => {
    setSearchPos(Math.max(0, searchHits.length - 1));
    // only when the term or the chat changes
  }, [searchQ, activeConv?.id]);
  const stepSearch = (dir: 1 | -1) =>
    setSearchPos((p) =>
      searchHits.length ? (p + dir + searchHits.length) % searchHits.length : 0,
    );
  useEffect(() => {
    const id = searchHits[searchPos];
    if (!id) return;
    // Make sure the match is rendered (older history is windowed), then bring it into view.
    const idx = messages.findIndex((m) => m.eventId === id);
    if (idx >= 0 && messages.length - idx > windowCount) setWindowCount(messages.length - idx + 10);
    requestAnimationFrame(() =>
      document.getElementById(`msg-${id}`)?.scrollIntoView({ behavior: "smooth", block: "center" }),
    );
  }, [searchPos, searchQ, searchHits.join(",")]);
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  const [caret, setCaret] = useState(0);
  const pendingCaret = useRef<number | null>(null);
  useLayoutEffect(() => {
    const at = pendingCaret.current;
    const el = composerRef.current;
    if (at === null || !el) return;
    pendingCaret.current = null;
    el.focus();
    el.setSelectionRange(at, at);
    setCaret(at);
  }, [draft]);
  // The field grows with its text (up to ~6 lines), like the phone's.
  useEffect(() => {
    const el = composerRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 150)}px`;
  }, [draft]);
  const isGroup = activeConv?.type === "group";
  // A group seen for the first time is an invitation (phone parity, review M1).
  const groupInvite = isGroup && Boolean(activeConv?.requestPending);
  const notMember =
    isGroup &&
    !!activeGroup &&
    !!selfKeyHex &&
    !activeGroup.members.some((m) => m.identityKeyHex.toLowerCase() === selfKeyHex.toLowerCase());
  const isRequest =
    !isGroup && Boolean(activeConv?.requestPending || activeContact?.requestState === "pending");
  // ---- reading position: first unread, settled rows, follow only at the bottom ----------------
  const messagesBoxRef = useRef<HTMLDivElement | null>(null);
  const initFor = useRef<string | null>(null);
  const settledIds = useRef<Set<string>>(new Set());
  const [firstUnread, setFirstUnread] = useState<{ id: string; count: number } | null>(null);
  const [newBelow, setNewBelow] = useState(0);
  const lastCount = useRef(0);

  const nearBottom = () => {
    const box = messagesBoxRef.current;
    return !box || box.scrollHeight - box.scrollTop - box.clientHeight < 160;
  };
  const toBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "auto", block: "end" });
    setNewBelow(0);
  };

  useEffect(() => {
    if (!activeConv) return;
    const loaded = messages.length > 0 && messages[0]!.conversationId === activeConv.id;
    if (initFor.current !== activeConv.id) {
      if (!loaded) {
        setFirstUnread(null);
        setNewBelow(0);
        return;
      }
      initFor.current = activeConv.id;
      settledIds.current = new Set(messages.map((m) => m.eventId));
      lastCount.current = messages.length;
      const incoming = messages.filter((m) => m.direction === "in");
      const target =
        unreadAtOpen > 0 ? incoming[Math.max(0, incoming.length - unreadAtOpen)] : undefined;
      setFirstUnread(
        target ? { id: target.eventId, count: Math.min(unreadAtOpen, incoming.length) } : null,
      );
      setNewBelow(0);
      // Long backlog: open at the first unread message; otherwise at the bottom.
      requestAnimationFrame(() => {
        if (target && unreadAtOpen > 4) {
          document
            .getElementById(`msg-${target.eventId}`)
            ?.scrollIntoView({ behavior: "auto", block: "start" });
        } else {
          messagesEndRef.current?.scrollIntoView({ behavior: "auto", block: "end" });
        }
      });
      return;
    }
    if (!loaded) return;
    const added = messages.length - lastCount.current;
    lastCount.current = messages.length;
    if (added <= 0) return;
    const last = messages[messages.length - 1]!;
    if (last.direction === "out" || nearBottom()) {
      requestAnimationFrame(() =>
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }),
      );
    } else {
      setNewBelow((n) => n + added);
    }
    // messagesEndRef is a stable ref object; it isn't a dependency.
  }, [messages, activeConv?.id, unreadAtOpen]);

  const [inviterName, setInviterName] = useState<string | null>(null);
  useEffect(() => {
    if (!groupInvite || !activeConv) {
      setInviterName(null);
      return;
    }
    let live = true;
    void getMeta(`group.pending.${activeConv.id}`).then((id) => {
      if (live) setInviterName(contacts.find((c) => c.id === id)?.displayName ?? null);
    });
    return () => {
      live = false;
    };
  }, [groupInvite, activeConv, contacts]);

  if (!activeConv) {
    return (
      <section className="pane pane-chat chat">
        <div className="empty">
          <div className="empty-card chat-welcome-card">
            <div className="empty-illus">
              <IconShield size={34} />
            </div>
            <h2>Unscanned. Uncontrolled.</h2>
            <p className="muted">
              End-to-end encrypted. Relays carry ciphertext — not readable chat. Pick a conversation
              or add a contact to begin.
            </p>
          </div>
        </div>
      </section>
    );
  }

  // In-chat search keeps the whole conversation and steps through the matches (phone parity).
  const filtered = messages;
  const windowed = visibleSlice(filtered, windowCount);
  const hiddenCount = Math.max(0, filtered.length - windowed.length);
  const keysChanged = activeContact?.verificationState === "changed";
  const canSend = !!draft.trim() && !keysChanged;
  /** Your own name for a group member (never the admin-supplied one when you know them). */
  function senderNameOf(m: MessageEvent): string | undefined {
    if (!isGroup || m.direction !== "in") return undefined;
    const byId = m.senderContactId ? contacts.find((c) => c.id === m.senderContactId) : undefined;
    if (byId) return byId.displayName;
    const member = activeGroup?.members.find(
      (x) => x.identityKeyHex.slice(0, 32).toLowerCase() === m.senderDevice.toLowerCase(),
    );
    const known = member
      ? contacts.find(
          (c) => c.messagingPublicKeyHex.toLowerCase() === member.identityKeyHex.toLowerCase(),
        )
      : undefined;
    return known?.displayName ?? member?.displayName ?? "Member";
  }

  /** Consecutive messages from the same person within 3 minutes form one run (one tail, one name). */
  function sameRun(a: MessageEvent | undefined, b: MessageEvent | undefined): boolean {
    return (
      !!a &&
      !!b &&
      a.direction === b.direction &&
      a.senderDevice === b.senderDevice &&
      Math.abs(b.createdAt - a.createdAt) < 3 * 60_000
    );
  }

  return (
    <section className="pane pane-chat chat">
      <header className="topbar">
        <button
          className="icon-btn"
          aria-label="Back to conversations"
          title="Back"
          onClick={onBack}
        >
          <IconBack />
        </button>
        <Avatar
          name={activeConv.title}
          size={38}
          verified={activeContact?.verificationState === "verified"}
          media={
            avatarUrl && activeContact?.avatar
              ? { url: avatarUrl, kind: activeContact.avatar.kind }
              : null
          }
        />
        <div
          className="grow chat-title"
          onContextMenu={(e) => {
            if (!onOpenDetails) return;
            e.preventDefault();
            onOpenDetails();
          }}
        >
          <strong>{activeConv.title}</strong>
          <div>
            {disappearTimer > 0 && (
              <span className="verify-chip timer-chip" title="Disappearing messages are on">
                ⏱ {disappearLabel(disappearTimer)}
              </span>
            )}
            {activeContact?.verificationState === "verified" ? (
              <span className="verify-chip ok">Verified</span>
            ) : activeContact?.verificationState === "changed" ? (
              <span className="verify-chip danger">Keys changed</span>
            ) : activeConv.type === "group" ? (
              <span className="verify-chip ok">Group</span>
            ) : (
              <button
                type="button"
                className="verify-chip improve"
                onClick={onToggleDetails}
                title="Optional — verify this contact to improve safety"
              >
                Improve safety
              </button>
            )}
          </div>
        </div>
        <button
          className="icon-btn"
          aria-label="Conversation details"
          title="Details"
          onClick={onToggleDetails}
        >
          <IconDetails />
        </button>
      </header>
      {activeContact?.verificationState === "changed" && (
        <div className="assurance" role="alert">
          This contact’s identity keys changed. Confirm the safety number out of band before
          continuing.
        </div>
      )}
      <div className="chat-search-bar">
        <IconSearch />
        <input
          value={chatQuery}
          onChange={(e) => setChatQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && searchHits.length) {
              e.preventDefault();
              stepSearch(e.shiftKey ? 1 : -1);
            }
          }}
          placeholder="Search in chat (local only)"
          aria-label="Search in chat"
        />
        {searchQ && (
          <span className="search-count" aria-live="polite">
            {searchHits.length ? `${searchPos + 1} of ${searchHits.length}` : "No results"}
          </span>
        )}
        {searchHits.length > 1 && (
          <>
            <button
              type="button"
              className="icon-btn small"
              aria-label="Older match"
              title="Older"
              onClick={() => stepSearch(-1)}
            >
              ↑
            </button>
            <button
              type="button"
              className="icon-btn small"
              aria-label="Newer match"
              title="Newer"
              onClick={() => stepSearch(1)}
            >
              ↓
            </button>
          </>
        )}
      </div>
      <div
        className="messages"
        aria-live="polite"
        ref={messagesBoxRef}
        onScroll={() => {
          if (newBelow && nearBottom()) setNewBelow(0);
        }}
      >
        {hiddenCount > 0 && (
          <div className="window-more">
            <button
              type="button"
              className="btn ghost small"
              onClick={() => setWindowCount((c) => nextWindow(c, filtered.length))}
            >
              Load earlier ({hiddenCount})
            </button>
          </div>
        )}
        {searchQ && searchHits.length === 0 && (
          <div className="chat-search-empty" role="status">
            <strong>No messages match</strong>
            <p className="muted">
              Local search only — nothing in this chat contains “{chatQuery.trim()}”.
            </p>
            <button type="button" className="btn secondary small" onClick={() => setChatQuery("")}>
              Clear search
            </button>
          </div>
        )}
        {groupMessagesByDay(windowed).map((group) => (
          <div key={group.day} style={{ display: "contents" }}>
            <div className="day-sep">{group.day}</div>
            {group.items.map((m, i) => {
              const quoted = m.replyToEventId
                ? messages.find((x) => x.eventId === m.replyToEventId)?.body
                : undefined;
              const runStart = !sameRun(group.items[i - 1], m);
              const runEnd = !sameRun(m, group.items[i + 1]);
              return (
                <div key={m.eventId} id={`msg-${m.eventId}`} style={{ display: "contents" }}>
                  {firstUnread?.id === m.eventId && (
                    <div className="unread-divider" role="separator">
                      {firstUnread.count === 1
                        ? "1 unread message"
                        : `${firstUnread.count} unread messages`}
                    </div>
                  )}
                  <MessageBubble
                    settled={settledIds.current.has(m.eventId)}
                    highlight={searchQ}
                    onReport={onReport && !isGroup ? () => onReport({ reported: m }) : undefined}
                    searchHit={searchHits[searchPos] === m.eventId || flashId === m.eventId}
                    msg={m}
                    runStart={runStart}
                    runEnd={runEnd}
                    senderName={runStart ? senderNameOf(m) : undefined}
                    replyPreview={quoted}
                    audioUrl={voiceUrlFor(m.eventId)}
                    onReply={() => onReply(m)}
                    onReact={(emoji) => onReact(m, emoji)}
                    onEdit={() => onEdit(m)}
                    onDeleteMe={() => onDeleteMe(m)}
                    onDeleteEveryone={() => onDeleteEveryone(m)}
                    onForward={() => onForward(m)}
                    onStar={() => onStar(m)}
                    onPin={() => onPin(m)}
                    onOpenViewOnce={() => onOpenViewOnce(m)}
                    onVotePoll={onVotePoll ? (optionId) => onVotePoll(m, optionId) : undefined}
                  />
                </div>
              );
            })}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
      {newBelow > 0 && (
        <button type="button" className="jump-latest" onClick={toBottom}>
          {newBelow === 1 ? "1 new message" : `${newBelow} new messages`} ↓
        </button>
      )}
      {isRequest && (
        <div className="request-bar" role="region" aria-label="Message request">
          <p className="request-title">
            <strong>{activeConv.title}</strong> isn&apos;t in your contacts
          </p>
          <p className="muted small">
            They have your invite link. Messages are end-to-end encrypted, but you haven&apos;t
            confirmed who they are — accept only if you know them.
          </p>
          <div className="request-actions">
            <button
              type="button"
              className="btn secondary danger"
              onClick={() => onDeclineRequest?.(true)}
            >
              Decline &amp; block
            </button>
            <button
              type="button"
              className="btn secondary"
              onClick={() => onDeclineRequest?.(false)}
            >
              Decline
            </button>
            <button type="button" className="btn" onClick={() => onAcceptRequest?.()}>
              Accept
            </button>
          </div>
          {onReport && (
            <button
              type="button"
              className="btn-text danger request-report"
              onClick={() => onReport({ fromRequest: true })}
            >
              Report &amp; block
            </button>
          )}
        </div>
      )}
      {groupInvite && (
        <div className="request-bar" role="region" aria-label="Group invitation">
          <p className="request-title">
            <strong>{inviterName ?? "A contact"}</strong> invited you to{" "}
            <strong>{activeConv.title}</strong>
          </p>
          <p className="muted small">
            Join only if you expected it from them. Until you join, you can read what is sent but
            not write.
          </p>
          <div className="request-actions">
            <button
              type="button"
              className="btn secondary danger"
              onClick={() => onDeclineGroupInvite?.(true)}
            >
              Decline &amp; block
            </button>
            <button
              type="button"
              className="btn secondary"
              onClick={() => onDeclineGroupInvite?.(false)}
            >
              Decline
            </button>
            <button type="button" className="btn" onClick={() => onAcceptGroupInvite?.()}>
              Join group
            </button>
          </div>
        </div>
      )}
      {notMember && !groupInvite && (
        <p className="composer-closed" role="status">
          You are no longer a member of this group.
        </p>
      )}
      {!isRequest && (replyTo || editing) && (
        <div className="composer-reply">
          <strong>{editing ? "Editing" : "Replying"}</strong>
          <span className="grow">{(editing?.body ?? replyTo?.body ?? "").slice(0, 80)}</span>
          <button type="button" className="btn ghost small" onClick={clearComposerMeta}>
            Cancel
          </button>
        </div>
      )}
      <div className="composer-wrap" hidden={isRequest || groupInvite || notMember}>
        {pickerTab && (
          <EmojiPicker
            initialTab={pickerTab}
            onPick={(e) => setDraft(draft + e)}
            onClose={() => setPickerTab(null)}
            onSendSticker={onSendSticker}
            onSendStickerFile={onSendStickerFile}
            onSendGifFile={onSendGifFile}
          />
        )}
        {keysChanged && (
          <p className="composer-hint warn" role="status" id="composer-send-hint">
            Sending paused until you confirm this contact’s safety number.
          </p>
        )}
        {!keysChanged && !draft.trim() && (
          <p className="composer-hint" id="composer-send-hint">
            Type a message to send, or hold the mic for a voice note.
          </p>
        )}
        <form
          className="composer"
          onSubmit={(e) => {
            e.preventDefault();
            if (keysChanged || !draft.trim()) return;
            onSend();
          }}
        >
          <div className="composer-tools" ref={toolsRef}>
            <button
              type="button"
              className={`icon-btn${attachOpen ? " active" : ""}`}
              aria-label="Attach"
              aria-expanded={attachOpen}
              title="Attach"
              onClick={() => {
                setAttachOpen((v) => !v);
                setMoreOpen(false);
                setPickerTab(null);
              }}
            >
              <IconAttach />
            </button>
            {attachOpen && (
              <div className="composer-menu" role="menu" aria-label="Attachments">
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => document.getElementById("veto-attach-image")?.click()}
                >
                  <IconImage size={18} />
                  Image
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => document.getElementById("veto-attach-camera")?.click()}
                >
                  <IconImage size={18} />
                  Camera
                </button>
                <input
                  id="veto-attach-camera"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  hidden
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onAttachImage(f);
                    e.target.value = "";
                    setAttachOpen(false);
                  }}
                />
                <input
                  id="veto-attach-image"
                  type="file"
                  accept="image/*,.png,.jpg,.jpeg,.webp,.gif"
                  hidden
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onAttachImage(f);
                    e.target.value = "";
                    setAttachOpen(false);
                  }}
                />
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => document.getElementById("veto-attach-doc")?.click()}
                >
                  <IconDoc size={18} />
                  Document
                </button>
                <input
                  id="veto-attach-doc"
                  type="file"
                  accept=".pdf,.txt,.docx,application/pdf,text/plain"
                  hidden
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onAttachDocument(f);
                    e.target.value = "";
                    setAttachOpen(false);
                  }}
                />
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => document.getElementById("veto-attach-viewonce")?.click()}
                >
                  <IconEye size={18} />
                  View once
                </button>
                <input
                  id="veto-attach-viewonce"
                  type="file"
                  accept="image/*,.png,.jpg,.jpeg,.webp,.gif"
                  hidden
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onViewOnceImage(f);
                    e.target.value = "";
                    setAttachOpen(false);
                  }}
                />
                {onCreatePoll && (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setAttachOpen(false);
                      onCreatePoll();
                    }}
                  >
                    <IconPoll size={18} />
                    Poll
                  </button>
                )}
              </div>
            )}
            <button
              type="button"
              className={`icon-btn${pickerTab === "emoji" ? " active" : ""}`}
              title="Emoji"
              aria-label="Emoji picker"
              aria-expanded={pickerTab === "emoji"}
              onClick={() => {
                setPickerTab((t) => (t === "emoji" ? null : "emoji"));
                setAttachOpen(false);
                setMoreOpen(false);
              }}
            >
              <IconEmoji />
            </button>
            <button
              type="button"
              className={`icon-btn${pickerTab === "stickers" ? " active" : ""}`}
              title="Stickers"
              aria-label="Sticker picker"
              aria-expanded={pickerTab === "stickers"}
              onClick={() => {
                setPickerTab((t) => (t === "stickers" ? null : "stickers"));
                setAttachOpen(false);
                setMoreOpen(false);
              }}
            >
              <IconSticker />
            </button>
            <button
              type="button"
              className={`icon-btn${moreOpen ? " active" : ""}`}
              title="More"
              aria-label="More actions"
              aria-expanded={moreOpen}
              onClick={() => {
                setMoreOpen((v) => !v);
                setAttachOpen(false);
                setPickerTab(null);
              }}
            >
              <IconMore />
            </button>
            {moreOpen && (
              <div className="composer-menu right" role="menu" aria-label="More chat actions">
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMoreOpen(false);
                    onDirect();
                  }}
                >
                  <IconDirect size={18} />
                  Direct path
                </button>
                <button
                  type="button"
                  role="menuitem"
                  disabled
                  title={CALL_FEATURE_STATUS.gateMessage}
                >
                  <IconPhone size={18} />
                  Call
                  <span className="menu-hint">Soon</span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMoreOpen(false);
                    setPickerTab("gifs");
                  }}
                >
                  <IconGif size={18} />
                  GIF / Stickers
                  <span className="menu-hint">Local + opt-in</span>
                </button>
              </div>
            )}
          </div>
          {isGroup && activeGroup && !groupInvite && !notMember && (
            <MentionPicker
              draft={draft}
              caret={caret}
              members={activeGroup.members.filter(
                (x) => x.identityKeyHex.toLowerCase() !== selfKeyHex.toLowerCase(),
              )}
              contacts={contacts}
              onPick={(next, at) => {
                // Caret placed right after the new value is in the field (before the next key).
                pendingCaret.current = at;
                setDraft(next);
              }}
            />
          )}
          <textarea
            ref={composerRef}
            className="composer-input"
            rows={1}
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setCaret(e.target.selectionStart ?? e.target.value.length);
            }}
            onSelect={(e) => setCaret((e.target as HTMLTextAreaElement).selectionStart ?? 0)}
            onKeyDown={(e) => {
              // Enter sends (Shift+Enter: new line) unless the setting says otherwise; never mid-IME.
              if (
                e.key === "Enter" &&
                !e.shiftKey &&
                !e.nativeEvent.isComposing &&
                loadEnterSends()
              ) {
                e.preventDefault();
                (e.currentTarget.form as HTMLFormElement | null)?.requestSubmit();
              }
            }}
            placeholder={
              keysChanged
                ? "Confirm safety number before sending"
                : editing
                  ? "Edit message"
                  : activeConv.type === "group"
                    ? "Message · @ to mention"
                    : "Message"
            }
            aria-label="Message"
            aria-describedby={keysChanged || !draft.trim() ? "composer-send-hint" : undefined}
            autoComplete="off"
            autoCapitalize="sentences"
            autoCorrect="off"
            spellCheck
            enterKeyHint={loadEnterSends() ? "send" : "enter"}
            inputMode="text"
            disabled={keysChanged}
          />
          {canSend ? (
            <button className="send-btn" type="submit" aria-label="Send message" title="Send">
              <IconSend />
            </button>
          ) : (
            <span
              className="composer-mic-slot"
              title={
                keysChanged
                  ? "Confirm safety number before sending"
                  : "Hold to record a voice note, or type to enable Send"
              }
            >
              <VoiceNoteButton
                disabled={keysChanged || (activeConv.type === "direct" && !activeContact)}
                onRecorded={onVoice}
                onError={onVoiceError}
              />
            </span>
          )}
        </form>
      </div>
    </section>
  );
}

/**
 * Groups: typing "@" offers the members; picking one writes their @name — the token the phone
 * app and other browsers recognise (whole name without spaces).
 */
function MentionPicker({
  draft,
  caret,
  members,
  contacts,
  onPick,
}: {
  draft: string;
  caret: number;
  members: GroupMember[];
  contacts: Contact[];
  onPick: (nextDraft: string, caretAt: number) => void;
}) {
  const before = draft.slice(0, Math.min(caret, draft.length));
  const m = /(^|\s)@([\p{L}\p{N}._'-]{0,32})$/u.exec(before);
  if (!m) return null;
  const partial = m[2]!.toLowerCase();
  const start = before.length - m[2]!.length - 1;
  const nameOf = (x: GroupMember) => {
    const c = contacts.find(
      (k) => k.messagingPublicKeyHex.toLowerCase() === x.identityKeyHex.toLowerCase(),
    );
    return c?.displayName ?? x.displayName;
  };
  const hits = members.filter(
    (x) =>
      mentionToken(x.displayName) &&
      (nameOf(x).toLowerCase().includes(partial) ||
        mentionToken(x.displayName).toLowerCase().startsWith(partial)),
  );
  if (hits.length === 0) return null;
  return (
    <div className="mention-picker" role="listbox" aria-label="Mention a member">
      {hits.slice(0, 6).map((x) => (
        <button
          key={x.identityKeyHex}
          type="button"
          role="option"
          aria-selected={false}
          className="mention-option"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            const token = `@${mentionToken(x.displayName)} `;
            onPick(
              draft.slice(0, start) + token + draft.slice(before.length),
              start + token.length,
            );
          }}
        >
          <strong>{nameOf(x)}</strong>
          <span className="muted">@{mentionToken(x.displayName)}</span>
        </button>
      ))}
    </div>
  );
}

function disappearLabel(sec: number): string {
  if (sec >= 86400) return sec % 86400 === 0 ? `${sec / 86400} d` : `${Math.round(sec / 3600)} h`;
  if (sec >= 3600) return `${Math.round(sec / 3600)} h`;
  if (sec >= 60) return `${Math.round(sec / 60)} min`;
  return `${sec} s`;
}
